/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState, useContext } from "react";
import Dropdown from "components/fields/dropdown";
import axios from "axios";
import SweetAlert2 from "react-sweetalert2";
import { Link } from "react-router-dom";
import { AiOutlineUser } from "react-icons/ai";
import { FiMenu } from "react-icons/fi";
import { useRouter } from "routes/hooks/useRouter";
import { Auth } from "aws-amplify";
import { Button } from "@chakra-ui/react";
import Clock from "./components/Clock";
import SwitchAccountModal from "./components/SwitchAccountModal";
import OpenTradeModal from "../../views/admin/shared/OpenTradeModal";
import { GrTransaction } from "react-icons/gr";
import { SubaccountContext } from "../../store/SubaccountProvider";

const appName = process.env.REACT_APP_NAME!;

const Navbar = (props: {
  isIdle: boolean;
  onOpenSidenav: () => void;
  brandText: string;
  secondary?: boolean | string;
  userPermissions: any | null;
}) => {
  const {
    subaccountBalance,
    setSubaccountBalance,
    isGettingBalance,
    setIsGettingBalance,
  } = useContext(SubaccountContext);
  const { onOpenSidenav, brandText, isIdle, userPermissions } = props;
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [swalProps, setSwalProps] = useState({});

  const router = useRouter();

  const currentUserRole = localStorage.getItem("userRole");
  const merchantId = localStorage.getItem("merchantId");
  const subaccountId = localStorage.getItem("subaccountId");
  const permissionKey = `${merchantId}-${subaccountId}`;

  const [isOpenOpenTradeModal, setIsOpenOpenTradeModal] = useState(false);
  const [isFetchingTradeAsset, setIsFetchingTradeAsset] = useState(false);
  const [fiatAssets, setFiatAssets] = useState<any>([]);
  const [cryptoAssets, setCryptoAssets] = useState<any>([]);
  const [assets, setAssets] = useState<any>([]);
  const [isSwitchAccountModalOpen, setIsSwitchAccountModalOpen] = useState(false);

  const showSwitchAccount = currentUserRole?.toLowerCase() !== "superadmin" && (() => {
    try {
      const userRoles = JSON.parse(localStorage.getItem("userRoles") || "[]");
      return userRoles.length >= 2;
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    const fetchData = async () => {
      if (currentUserRole === "Subaccount") {
        setIsFetchingTradeAsset(true);
        await fetchAssets();
        setIsFetchingTradeAsset(false);
      }
    };

    fetchData();
  }, []); // eslint-disable-line

  const handleLogout = async () => {
    try {
      await Auth.signOut();

      localStorage.clear();

      router.push("/auth/sign-in");
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };

  useEffect(() => {
    const fetchUserEmail = async () => {
      try {
        const userInfo = await Auth.currentAuthenticatedUser();
        setUserEmail(userInfo.attributes.email);
        setUserName(userInfo.username);
      } catch (error) {
        console.error("Error fetching user info: ", error);
      }
    };

    fetchUserEmail();
  }, []);

  const DropdownContent = ({
    userName,
    userEmail,
    currentUserRole,
    handleLogout,
    setOpenWrapper,
    showSwitchAccount,
    onSwitchAccount,
  }: {
    userName: string;
    userEmail: string;
    currentUserRole: string;
    handleLogout: () => void;
    setOpenWrapper: any;
    showSwitchAccount: boolean;
    onSwitchAccount: () => void;
  }) => {
    const handleGoToMyProfile = () => {
      setOpenWrapper(false);
      router.push(`/${currentUserRole.toLowerCase()}/my-profile`);
    };

    return (
      <div className="custom-shadow flex w-auto min-w-[230px] flex-col justify-start rounded-sm bg-white bg-cover bg-no-repeat">
        <div className="ml-4 mr-4 mt-3">
          <div className="items-center gap-2 overflow-x-auto whitespace-nowrap">
            <p className="text-md font-bold capitalize text-navy-700 dark:text-white">
              Hi, {userName}
            </p>
            <p className="overflow-x-auto whitespace-nowrap text-xs text-gray-800">
              {userEmail}
            </p>
          </div>
        </div>
        <div className="mt-3 h-px w-full bg-gray-200 dark:bg-white/20 " />

        <div className="ml-4 mr-4 mt-3 mb-4 flex flex-col text-sm">
          <span
            onClick={handleGoToMyProfile}
            className="mt-3 cursor-pointer text-sm font-medium text-primary-500 hover:text-primary-700"
          >
            Profile
          </span>

          {showSwitchAccount && (
            <span
              onClick={() => {
                setOpenWrapper(false);
                onSwitchAccount();
              }}
              className="mt-3 cursor-pointer text-sm font-medium text-primary-500 hover:text-primary-700"
            >
              Switch Account
            </span>
          )}

          <span
            onClick={(e) => {
              e.preventDefault();
              handleLogout();
            }}
            className="mt-3 cursor-pointer text-sm font-medium text-red-500 hover:text-red-500"
          >
            Sign Out
          </span>
        </div>
      </div>
    );
  };

  const fetchAssets = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/trade/assets`
      );
      const data = response.data.assets.payload;

      const mergedAssets: any[] = [];
      const cryptoAssets: any[] = [];

      // Iterate through each asset type
      Object.keys(data).forEach((assetCode) => {
        const methods = data[assetCode];

        // Check if methods array exists and has items
        if (methods && methods.length > 0) {
          const firstMethod = methods[0];

          if (firstMethod.crypto === false) {
            // Fiat asset - add only once
            mergedAssets.push({
              id: assetCode,
              code: assetCode,
              name: assetCode,
              crypto: false,
              decimal: firstMethod.decimal,
              min_trade: firstMethod.min_trade,
              methods: methods,
            });
          } else {
            // Crypto asset - add ALL asset-network combinations
            if (firstMethod.networks && firstMethod.networks.length > 0) {
              firstMethod.networks.forEach((network: any) => {
                const uniqueId = `${assetCode}-${network.network}`;

                // Add to mergedAssets with network display name
                mergedAssets.push({
                  id: uniqueId,
                  code: assetCode,
                  method_id: firstMethod.method_id,
                  method_name: firstMethod.method_name,
                  name: `${assetCode} (${network.display_name})`, // "USDT (TRC20)"
                  crypto: true,
                  decimal: firstMethod.decimal,
                  min_trade: firstMethod.min_trade,
                  network: network.network,
                  network_pk: network.network_pk,
                  display_name: network.display_name,
                  chain: network.chain,
                  is_default: network.is_default,
                  asset_pk: firstMethod.asset_pk || assetCode,
                });

                // Also add to cryptoAssets
                cryptoAssets.push({
                  id: uniqueId,
                  code: assetCode,
                  method_id: firstMethod.method_id,
                  method_name: firstMethod.method_name,
                  name: `${assetCode} (${network.display_name})`, // "USDT (TRC20)"
                  network: network.network,
                  network_pk: network.network_pk,
                  display_name: network.display_name,
                  crypto: true,
                  decimal: firstMethod.decimal,
                  min_trade: firstMethod.min_trade,
                  chain: network.chain,
                  is_default: network.is_default,
                  asset_pk: firstMethod.asset_pk || assetCode,
                });
              });
            }
          }
        }
      });

      setFiatAssets(mergedAssets.filter((asset) => !asset.crypto));
      setCryptoAssets(cryptoAssets); // All asset-network combos
      setAssets(mergedAssets); // Now also includes all asset-network combos
    } catch (error) {
      console.error("Error fetching data", error);
      setFiatAssets([]);
      setCryptoAssets([]);
      setAssets([]);
    }
  };

  const handleOpenTradeModal = () => {
    setIsOpenOpenTradeModal(true);
  };

  const handleCloseTradeModal = () => {
    setIsOpenOpenTradeModal(false);
  };

  return (
    <>
      {isSwitchAccountModalOpen && (
        <SwitchAccountModal
          isOpen={isSwitchAccountModalOpen}
          onClose={() => setIsSwitchAccountModalOpen(false)}
        />
      )}
      {isOpenOpenTradeModal && fiatAssets && cryptoAssets && (
        <OpenTradeModal
          isOpen={isOpenOpenTradeModal}
          onClose={handleCloseTradeModal}
          assets={assets}
          fiatAssets={fiatAssets}
          cryptoAssets={cryptoAssets}
          setSwalProps={setSwalProps}
          isFetchingTradeAsset={isFetchingTradeAsset}
          subaccountBalance={subaccountBalance}
          setSubaccountBalance={setSubaccountBalance}
          isGettingBalance={isGettingBalance}
          setIsGettingBalance={setIsGettingBalance}
        />
      )}
      <nav className="z-40 flex flex-wrap items-center justify-between gap-6 bg-white px-8 py-3 shadow-sm md:flex-nowrap ">
        {/* <div className="flex w-full flex-col md:w-auto">
          <h1 className="mt-1 text-2xl font-bold capitalize leading-snug tracking-tight text-navy-700 dark:text-white">
            <Link to="#" className="hover:text-navy-800 dark:hover:text-white">
              {brandText}
            </Link>
          </h1>
          <Clock />
        </div> */}
        <div className="flex w-full flex-row items-center gap-3 md:w-auto">
          <button
            onClick={onOpenSidenav}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-gray-600 shadow-sm transition hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:text-white dark:shadow-none xl:hidden"
            aria-label="Open menu"
          >
            <FiMenu className="h-7 w-7" />
          </button>
          <div className="flex w-full flex-col md:w-auto">
            <h1 className="text-md mt-1 flex items-center gap-2 font-medium leading-snug text-gray-600 dark:text-gray-400 md:text-sm">
              <Link to="/dashboard" className="hover:text-gray-800">
                Payswiftly
              </Link>
              <span className="text-gray-400">/</span>
              <span className="text-gray-700 dark:text-white">{brandText}</span>
            </h1>

            <Clock />
          </div>
        </div>

        {!isIdle && (
          <div className="flex w-full items-center justify-between gap-4 md:w-auto">
            <div
              id="navbar-profile"
              className="relative flex w-full items-center justify-between gap-3"
            >
              {currentUserRole !== "SuperAdmin" &&
                userPermissions[permissionKey].openTrade && (
                  <Button
                    isLoading={
                      isFetchingTradeAsset || subaccountBalance.length === 0
                    }
                    loadingText="Preparing…"
                    onClick={handleOpenTradeModal}
                    size="sm"
                    className="btn-primary inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 active:translate-y-[1px] disabled:opacity-60 md:px-5"
                  >
                    <GrTransaction className="h-4 w-4" />
                    <span className="md:inline">Open Trade</span>
                  </Button>
                )}

              <Dropdown
                button={
                  <button
                    className="inline-flex h-10 items-center gap-2 rounded-lg px-2 pr-3 text-gray-800 transition hover:bg-gray-50"
                    aria-label="Open user menu"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-white">
                      <AiOutlineUser className="h-4 w-4" />
                    </span>

                    <span className="max-w-[140px] truncate text-sm font-medium capitalize">
                      {userName || "Account"}
                    </span>

                    <svg
                      className="h-4 w-4 text-gray-400 dark:text-white/50"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                }
                children={(setOpenWrapper) => (
                  <DropdownContent
                    setOpenWrapper={setOpenWrapper}
                    userName={userName}
                    userEmail={userEmail}
                    currentUserRole={currentUserRole}
                    handleLogout={handleLogout}
                    showSwitchAccount={showSwitchAccount}
                    onSwitchAccount={() => setIsSwitchAccountModalOpen(true)}
                  />
                )}
                classNames="py-2 top-11 right-0 !w-[240px]"
              />
            </div>
          </div>
        )}
      </nav>
      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default Navbar;
